import { ImageIcon, Sparkles } from 'lucide-react';

import type { ReceiptBlockState, ScanAvailability } from '@splitfiesta/core';
import { isPdfReceiptUrl, isRetryableFailure } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';

import { ScanAvailabilityLine } from '@/components/expenses/receipt/ReceiptActionRows';
import { ReceiptPdfTile } from '@/components/expenses/receipt/ReceiptPdfTile';

export type ThumbnailRowState = Exclude<ReceiptBlockState, { kind: 'actions' }>;

interface ReceiptThumbnailRowProps {
  state: ThumbnailRowState;
  availability: ScanAvailability;
  /** Itemizing is allowed here, so the provider hint mentions the items. */
  itemsAllowed?: boolean;
  onView: (url: string) => void;
  onReadWithAi: () => void;
  onTryAgain: () => void;
  onReplace: () => void;
  onRemove: () => void;
  onRestore: () => void;
}

const actionClass =
  'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium disabled:cursor-not-allowed disabled:opacity-50';
const neutralAction = `${actionClass} border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-800`;
const aiAction = `${actionClass} border-primary-500 text-gray-900 hover:bg-primary-50 dark:border-primary-400 dark:text-gray-100 dark:hover:bg-primary-500/15`;

/** The failure text's full key: the reader-unavailable one lives in `errors`. */
export function failureMessageKey(
  messageKey: Extract<ThumbnailRowState, { kind: 'failed' }>['messageKey']
): string {
  return messageKey.startsWith('errors:')
    ? messageKey
    : `expenses:${messageKey}`;
}

function imageUrl(state: ThumbnailRowState): string {
  return state.kind === 'saved' ? state.url : state.localUri;
}

function isPdf(state: ThumbnailRowState): boolean {
  return state.kind === 'saved'
    ? isPdfReceiptUrl(state.url)
    : state.fileKind === 'pdf';
}

const tileClass =
  'h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-800';

/** The photo, what happened to it, and what can be done next. */
export function ReceiptThumbnailRow({
  state,
  availability,
  itemsAllowed = false,
  onView,
  onReadWithAi,
  onTryAgain,
  onReplace,
  onRemove,
  onRestore,
}: ReceiptThumbnailRowProps) {
  const { t } = useTranslation();
  const offline = availability.kind === 'offline';
  const reading = state.kind === 'reading';
  const url = imageUrl(state);
  const pdf = isPdf(state);
  const fileName = pdf && state.kind !== 'saved' ? state.fileName : undefined;

  const title =
    state.kind === 'uploading'
      ? t('expenses:scan.uploading')
      : state.kind === 'reading'
        ? t('expenses:scan.reading')
        : state.kind === 'read'
          ? t('expenses:scan.readByAi')
          : t('expenses:scan.attached');
  const hint =
    state.kind === 'reading'
      ? t('expenses:scan.readingHint')
      : state.kind === 'read'
        ? t('expenses:scan.attached')
        : null;

  const readDisabled =
    state.kind === 'attached' &&
    (!state.readable || offline || availability.kind === 'exhausted');

  return (
    <div>
      <div className="flex flex-wrap items-start gap-3">
        {reading ? (
          <div
            data-testid="receipt-shimmer"
            className="h-16 w-16 shrink-0 overflow-hidden rounded-lg bg-gray-200 dark:bg-gray-700 animate-pulse motion-reduce:animate-none"
          >
            {pdf ? (
              <ReceiptPdfTile />
            ) : url ? (
              <img
                src={url}
                alt={t('expenses:photo.receiptPreview')}
                className="h-full w-full object-cover opacity-70"
              />
            ) : null}
          </div>
        ) : pdf && state.kind !== 'saved' ? (
          // A draft PDF exists only as a local blob, which the viewer never
          // embeds; it opens once saved, from its CDN URL.
          <div className={tileClass}>
            <ReceiptPdfTile />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => onView(url)}
            disabled={!url}
            aria-label={t('expenses:scan.viewReceipt')}
            className={tileClass}
          >
            {pdf ? (
              <ReceiptPdfTile />
            ) : url ? (
              <img
                src={url}
                alt={t('expenses:photo.receiptPreview')}
                className="h-full w-full object-cover"
              />
            ) : (
              <ImageIcon
                aria-hidden="true"
                className="m-auto h-6 w-6 text-gray-400"
              />
            )}
          </button>
        )}
        <div className="min-w-[45%] flex-1">
          <p className="text-base font-semibold text-gray-900 dark:text-gray-100">
            {title}
          </p>
          {fileName ? (
            <p className="break-all text-sm text-gray-700 dark:text-gray-300">
              {fileName}
            </p>
          ) : null}
          {hint ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">{hint}</p>
          ) : null}
        </div>
      </div>

      {state.kind === 'failed' ? (
        <p
          data-testid="receipt-failure-banner"
          className="mt-3 rounded-lg bg-error-50 px-3 py-2 text-sm text-error-700 dark:bg-error-900/20 dark:text-error-300"
        >
          {t(failureMessageKey(state.messageKey))}
        </p>
      ) : null}

      {reading ? null : (
        <div className="mt-3 flex flex-wrap gap-2">
          {state.kind === 'read' ? (
            <button type="button" onClick={onRestore} className={neutralAction}>
              {t('expenses:scan.restore')}
            </button>
          ) : null}
          {state.kind === 'attached' ? (
            <button
              type="button"
              onClick={onReadWithAi}
              disabled={readDisabled}
              className={aiAction}
            >
              <Sparkles
                aria-hidden="true"
                className="h-4 w-4 shrink-0 text-ai-accent dark:text-ai-accent-dark"
              />
              {t('expenses:scan.readWithAi')}
            </button>
          ) : null}
          {state.kind === 'failed' && isRetryableFailure(state.messageKey) ? (
            <button
              type="button"
              onClick={onTryAgain}
              disabled={offline}
              className={neutralAction}
            >
              {t('expenses:scan.tryAgain')}
            </button>
          ) : null}
          {state.kind === 'uploading' ? null : (
            <button
              type="button"
              onClick={onReplace}
              disabled={offline}
              className={neutralAction}
            >
              {t('expenses:scan.replace')}
            </button>
          )}
          <button type="button" onClick={onRemove} className={neutralAction}>
            {t('expenses:scan.remove')}
          </button>
        </div>
      )}

      {state.kind === 'attached' ? (
        <p
          data-testid="receipt-scan-hint"
          className="mt-2 text-sm text-gray-500 dark:text-gray-400"
        >
          {itemsAllowed
            ? t('expenses:scan.scanWithAiHintItems')
            : t('expenses:scan.scanWithAiHint')}
        </p>
      ) : null}

      {state.kind === 'attached' || offline ? (
        <div className="mt-2">
          <ScanAvailabilityLine availability={availability} />
        </div>
      ) : null}
    </div>
  );
}
